import api from '@/helpers/api_helper';
import { apiUrls } from '@/constant/configure';

/**
 * 인스타DM 자동화 이용권 — 충전·조회(mall 백엔드).
 * 결제는 NicePay 2-step: ① POST /orders(주문+서명) → 결제창(requestNicepayAuth) → ② POST /orders/confirm(승인·적립).
 * 인증은 api_helper 인터셉터가 Bearer 토큰을 자동 주입(userId는 토큰에서). 참고: 자동화이용권_클라이언트_가이드.md
 * (남은 개수 remaining 은 studio 자동화 목록에서 제공 — 이 서비스 아님)
 */
const MALL = { baseURL: apiUrls.mall };
const PREFIX = '/api/v1/automation-pass';

const throwMessage = (e: unknown): never => {
    if (typeof e === 'string') throw { message: e };
    throw e;
};

export interface AutomationPassPackage {
    code: string;
    name: string;
    credits: number; // 이용권 개수
    price: number; // 웹가
    iapPrice?: number; // 앱가(웹 미사용)
    appleProductId?: string;
    googleProductId?: string;
}

/** ① POST /orders 응답 — NicePay 결제창 실행용 주문+서명. */
export interface AutomationPassOrderInit {
    orderId: string;
    packageCode: string;
    credits: number;
    amount: number;
    GoodsName: string;
    MID: string;
    Moid: string;
    Amt: string;
    EdiDate: string;
    SignData: string;
}

/** ② POST /orders/confirm 요청 — 결제창 인증결과. */
export interface AutomationPassConfirmBody {
    packageCode: string;
    TID: string;
    AuthToken: string;
    MID: string;
    Amt: string;
    EdiDate: string;
    NextAppURL: string;
}

/** ② POST /orders/confirm 응답 — 승인·적립 후 잔액. */
export interface AutomationPassConfirmResult {
    balance: number;
    purchased: number;
    alreadyApplied: boolean;
}

export interface AutomationPassLedgerItem {
    id: string;
    type: string; // PURCHASE 등
    amount: number;
    balanceAfter: number;
    referenceType: string | null;
    referenceId: string | null;
    description: string | null;
    createdAt: string;
}

/** 보유 이용권 수. */
export const getPassBalance = async (): Promise<number> => {
    try {
        const res = await api.get(`${PREFIX}/balance`, MALL);
        return res.data?.balance ?? 0;
    } catch (e) {
        return throwMessage(e);
    }
};

/** 이용권 팩 목록(웹가). */
export const getPassPackages = async (): Promise<AutomationPassPackage[]> => {
    try {
        const res = await api.get(`${PREFIX}/packages`, MALL);
        return res.data?.packages ?? [];
    } catch (e) {
        return throwMessage(e);
    }
};

/** ① 주문 생성 + 결제창 서명. */
export const createPassOrder = async (packageCode: string): Promise<AutomationPassOrderInit> => {
    try {
        const origin = window.location.origin;
        const res = await api.post(`${PREFIX}/orders`, { packageCode, origin }, MALL);
        return res.data;
    } catch (e) {
        return throwMessage(e);
    }
};

/** ② 결제창 인증결과 전달 → 승인·적립. 멱등: 같은 결제 재confirm 시 purchased:0, alreadyApplied:true. */
export const confirmPassOrder = async (body: AutomationPassConfirmBody): Promise<AutomationPassConfirmResult> => {
    try {
        const res = await api.post(`${PREFIX}/orders/confirm`, body, MALL);
        return res.data;
    } catch (e) {
        return throwMessage(e);
    }
};

/** 충전 내역(선택). */
export const getPassLedger = async (params?: {
    offset?: number;
    limit?: number;
}): Promise<{ items: AutomationPassLedgerItem[]; total: number }> => {
    try {
        const query = { offset: params?.offset ?? 0, limit: params?.limit ?? 20 };
        const res = await api.get(`${PREFIX}/ledger`, { ...MALL, params: query });
        return { items: res.data?.items ?? [], total: res.data?.total ?? 0 };
    } catch (e) {
        return throwMessage(e);
    }
};
